// Cálculos derivados del estado: validación, masa, estabilidad y petición de simulación.
import {
  computeMass,
  listMounts,
  referenceDiameter,
  stability,
  toMassProfile,
  totalLength,
  validateRocket,
  hasErrors,
  type Issue,
  type MassResult,
  type MotorMassProfile,
  type Motor,
  type Rocket,
  type Stability,
} from '@openrocket-web/physics';
import { configOf, type AppState, type Simulation } from './app';
import { applyConfig } from './configs';
import { findMotor } from './motors';
import type { RunRequest } from '../worker/protocol';

export interface Analysis {
  readonly issues: readonly Issue[];
  readonly errors: readonly Issue[];
  readonly valid: boolean;
  readonly length: number;
  readonly refDiameter: number;
  readonly mass: MassResult | undefined;
  readonly stability: Stability | undefined;
}

export function motorResolver(library: readonly Motor[]): (id: string) => MotorMassProfile | undefined {
  return (id) => {
    const m = findMotor(library, id);
    return m === undefined ? undefined : toMassProfile(m);
  };
}

export function analyze(rocket: Rocket, library: readonly Motor[]): Analysis {
  const resolver = motorResolver(library);
  const issues = validateRocket(rocket, { motors: resolver });
  const errors = issues.filter((i) => i.severity === 'error');
  const valid = !hasErrors(issues);
  const length = totalLength(rocket);
  const refDiameter = referenceDiameter(rocket);
  if (rocket.stack.length === 0) return { issues, errors, valid, length, refDiameter, mass: undefined, stability: undefined };
  // La masa y la estabilidad se calculan con lo que haya, aunque el diseño tenga errores de motor.
  let mass: MassResult | undefined;
  let stab: Stability | undefined;
  try {
    mass = computeMass(rocket, { motors: resolver });
    stab = stability(rocket, mass.total.cgX);
  } catch {
    mass = undefined;
    stab = undefined;
  }
  return { issues, errors, valid, length, refDiameter, mass, stability: stab };
}

/** Petición de simulación. Los motores se envían con el id que usa el diseño, no el de la biblioteca. */
export function buildRequest(s: Pick<AppState, 'rocket' | 'motors' | 'configs' | 'activeConfigId'>, sim: Simulation): RunRequest {
  // Cada simulación usa su configuración de motor, que puede no ser la que muestra el diseño.
  const rocket = applyConfig(s.rocket, configOf(s, sim));
  const used: Motor[] = [];
  for (const m of listMounts(rocket)) {
    if (m.mark.motor === undefined) continue;
    const found = findMotor(s.motors, m.mark.motor.motorId);
    if (found !== undefined) used.push({ ...found, id: m.mark.motor.motorId });
  }
  return {
    rocket,
    motors: used,
    launch: sim.launch,
    wind: { speed: sim.wind.speed, direction: sim.wind.direction, turbulenceIntensity: sim.wind.turbulenceIntensity, seed: sim.sim.seed },
    roughness: sim.sim.roughness,
    timeStep: sim.sim.timeStep,
    seed: sim.sim.seed,
    perturbation: sim.sim.perturbation ? 1e-3 : 0,
  };
}
